// src/features/streams/components/simulcast/DestinationEditorModal.tsx

import { Modal, Stack, Text, SimpleGrid, UnstyledButton, Box, Group } from '@mantine/core';
import { useHover } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import { YouTubeLockup, FacebookLiveLockup, RtmpLockup } from './PlatformLogos';
import type { ISimulcastAllData } from '@/lib/types/Simulcast';
import type { StreamViewData } from '@/lib/types/Stream';
import { SimulcastTransforms } from '../../simulcast/simulcastTransforms';
import {
  useCreateSimulcastRtmp,
  useUpdateSimulcastRtmp,
  useUpdateSimulcastFB,
  useUpdateSimulcastYT,
} from '../../useSimulcastMutations';
import { RtmpDestinationForm } from './RtmpDestinationForm';
import { FacebookDestinationFlow } from './FacebookDestinationFlow';
import { FacebookEditForm } from './FacebookEditForm';
import { YouTubeDestinationFlow } from './YouTubeDestinationFlow';
import { YouTubeEditForm } from './YouTubeEditForm';
import type { RtmpFormData, FBEditFormData, YTEditFormData } from '../../schemas/SimulcastForm.schema';

// --- Modal state types ---

export type ModalState =
  | { mode: 'closed' }
  | { mode: 'create-select-type' }
  | { mode: 'create-rtmp' }
  | { mode: 'edit-rtmp'; simulcast: ISimulcastAllData }
  | { mode: 'create-facebook' }
  | { mode: 'edit-facebook'; simulcast: ISimulcastAllData }
  | { mode: 'reconnect-facebook'; simulcast: ISimulcastAllData }
  | { mode: 'create-youtube' }
  | { mode: 'edit-youtube'; simulcast: ISimulcastAllData }
  | { mode: 'reconnect-youtube'; simulcast: ISimulcastAllData };

interface DestinationEditorModalProps {
  state: ModalState;
  onClose: () => void;
  onChangeMode: (state: ModalState) => void;
  stream: StreamViewData;
}

/** Platform type selection card for the create flow. */
function PlatformTypeCard({
  logo,
  color,
  title,
  description,
  onClick,
}: {
  logo: React.ReactNode;
  color: string;
  title: string;
  description: string;
  onClick: () => void;
}) {
  const { hovered, ref } = useHover();

  return (
    <UnstyledButton
      ref={ref}
      onClick={onClick}
      px="lg"
      py="xl"
      style={{
        border: `1px solid var(--mantine-color-${hovered ? `${color}-4` : 'gray-3'})`,
        borderRadius: 'var(--mantine-radius-md)',
        transition: 'border-color 150ms, background-color 150ms',
        backgroundColor: hovered ? `var(--mantine-color-${color}-0)` : 'var(--mantine-color-gray-1)',
      }}
    >
      <Stack align="center" gap="md">
        <Box
          w="100%"
          h={70}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {logo}
        </Box>
        <Text fw={600}>{title}</Text>
        <Text c="dimmed" size="sm" ta="center">{description}</Text>
      </Stack>
    </UnstyledButton>
  );
}

/** Step 1: Select destination type. */
function TypeSelector({ onSelect }: { onSelect: (mode: ModalState) => void }) {
  return (
    <Stack gap="lg">
      <Text fw={500}>Where do you want to stream?</Text>
      <SimpleGrid cols={3}>
        <PlatformTypeCard
          logo={<RtmpLockup />}
          color="gray"
          title="Custom RTMP"
          description="Stream to any RTMP endpoint"
          onClick={() => onSelect({ mode: 'create-rtmp' })}
        />
        <PlatformTypeCard
          logo={<FacebookLiveLockup />}
          color="blue"
          title="Facebook Live"
          description="Stream to your page or group"
          onClick={() => onSelect({ mode: 'create-facebook' })}
        />
        <PlatformTypeCard
          logo={<YouTubeLockup />}
          color="red"
          title="YouTube Live"
          description="Stream to your channel"
          onClick={() => onSelect({ mode: 'create-youtube' })}
        />
      </SimpleGrid>
    </Stack>
  );
}

function getModalTitle(state: ModalState): string {
  switch (state.mode) {
    case 'create-select-type':
      return 'Add Destination';
    case 'create-rtmp':
      return 'Add RTMP Destination';
    case 'edit-rtmp':
      return 'Edit RTMP Destination';
    case 'create-facebook':
      return 'Add Facebook Live Destination';
    case 'edit-facebook':
      return 'Edit Facebook Live Destination';
    case 'reconnect-facebook':
      return 'Reconnect Facebook';
    case 'create-youtube':
      return 'Add YouTube Live Destination';
    case 'edit-youtube':
      return 'Edit YouTube Live Destination';
    case 'reconnect-youtube':
      return 'Reconnect YouTube';
    default:
      return '';
  }
}

export function DestinationEditorModal({
  state,
  onClose,
  onChangeMode,
  stream,
}: DestinationEditorModalProps) {
  const mediaId = stream.media.mediaId;
  const createRtmp = useCreateSimulcastRtmp(mediaId);
  const updateRtmp = useUpdateSimulcastRtmp(mediaId);
  const updateFB = useUpdateSimulcastFB(mediaId);
  const updateYT = useUpdateSimulcastYT(mediaId);

  const isOpen = state.mode !== 'closed';
  const showBackButton = state.mode !== 'create-select-type';

  // Prevent accidental close during multi-step flows (OAuth, processing, form filling)
  // FB/YT flows own their own mutations internally — the modal can't check their pending state.
  // Safer to block outside-click/escape for all active flow modes and let users use Back/X explicitly.
  const isInFlowMode =
    state.mode === 'create-facebook' ||
    state.mode === 'create-youtube' ||
    state.mode === 'reconnect-facebook' ||
    state.mode === 'reconnect-youtube';
  const isAnyMutationPending =
    createRtmp.isPending || updateRtmp.isPending || updateFB.isPending || updateYT.isPending;

  const handleBack = () => {
    // For create flows, go back to type selection
    if (state.mode.startsWith('create-')) {
      onChangeMode({ mode: 'create-select-type' });
    } else {
      onClose();
    }
  };

  const handleCreateRtmp = (data: RtmpFormData) => {
    const req = SimulcastTransforms.transformFormToRtmpUpdateReq(data);
    createRtmp.mutate(req, {
      onSuccess: () => {
        notifications.show({
          title: 'Destination Created',
          message: `"${data.label}" has been added.`,
          color: 'green',
        });
        onClose();
      },
      onError: (err) => {
        notifications.show({
          title: 'Error',
          message: err.message,
          color: 'red',
        });
      },
    });
  };

  const handleUpdateRtmp = (data: RtmpFormData) => {
    if (state.mode !== 'edit-rtmp') return;
    const simulcastId = state.simulcast.core.simulcastId;
    const req = SimulcastTransforms.transformFormToRtmpUpdateReq(data);
    updateRtmp.mutate(
      { simulcastId, req },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Destination Updated',
            message: `"${data.label}" has been saved.`,
            color: 'green',
          });
          onClose();
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
      },
    );
  };

  const handleUpdateFB = (data: FBEditFormData) => {
    if (state.mode !== 'edit-facebook') return;
    const simulcastId = state.simulcast.core.simulcastId;
    const req = SimulcastTransforms.transformFBEditFormToUpdateReq(data);
    updateFB.mutate(
      { simulcastId, req },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Destination Updated',
            message: `"${data.label}" has been saved.`,
            color: 'green',
          });
          onClose();
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
      },
    );
  };

  const handleUpdateYT = (data: YTEditFormData) => {
    if (state.mode !== 'edit-youtube') return;
    const simulcastId = state.simulcast.core.simulcastId;
    const req = SimulcastTransforms.transformYTEditFormToUpdateReq(data);
    updateYT.mutate(
      { simulcastId, req },
      {
        onSuccess: () => {
          notifications.show({
            title: 'Destination Updated',
            message: `"${data.label}" has been saved.`,
            color: 'green',
          });
          onClose();
        },
        onError: (err) => {
          notifications.show({
            title: 'Error',
            message: err.message,
            color: 'red',
          });
        },
      },
    );
  };

  const renderContent = () => {
    switch (state.mode) {
      case 'create-select-type':
        return <TypeSelector onSelect={onChangeMode} />;

      case 'create-rtmp':
        return (
          <RtmpDestinationForm
            onSubmit={handleCreateRtmp}
            onCancel={handleBack}
            isPending={createRtmp.isPending}
            submitLabel="Create"
          />
        );

      case 'edit-rtmp':
        return (
          <RtmpDestinationForm
            defaultValues={SimulcastTransforms.transformRtmpToFormData(state.simulcast)}
            onSubmit={handleUpdateRtmp}
            onCancel={onClose}
            isPending={updateRtmp.isPending}
            submitLabel="Save Changes"
          />
        );

      case 'create-facebook':
        return (
          <FacebookDestinationFlow
            mode="create"
            stream={stream}
            onClose={onClose}
            onBack={handleBack}
          />
        );

      case 'reconnect-facebook':
        return (
          <FacebookDestinationFlow
            mode="reconnect"
            stream={stream}
            simulcast={state.simulcast}
            onClose={onClose}
            onBack={onClose}
          />
        );

      case 'edit-facebook':
        return (
          <FacebookEditForm
            simulcast={state.simulcast}
            stream={stream}
            onSubmit={handleUpdateFB}
            onCancel={onClose}
            isPending={updateFB.isPending}
          />
        );

      case 'create-youtube':
        return (
          <YouTubeDestinationFlow
            mode="create"
            stream={stream}
            onClose={onClose}
            onBack={handleBack}
          />
        );

      case 'reconnect-youtube':
        return (
          <YouTubeDestinationFlow
            mode="reconnect"
            stream={stream}
            simulcast={state.simulcast}
            onClose={onClose}
            onBack={onClose}
          />
        );

      case 'edit-youtube':
        return (
          <YouTubeEditForm
            simulcast={state.simulcast}
            stream={stream}
            onSubmit={handleUpdateYT}
            onCancel={onClose}
            isPending={updateYT.isPending}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        <Group gap="xs">
          {showBackButton && (
            <UnstyledButton onClick={handleBack} style={{ display: 'flex', alignItems: 'center' }}>
              <IconArrowLeft size={18} />
            </UnstyledButton>
          )}
          <Text fw={600}>{getModalTitle(state)}</Text>
        </Group>
      }
      size="lg"
      closeOnClickOutside={!isInFlowMode && !isAnyMutationPending}
      closeOnEscape={!isInFlowMode && !isAnyMutationPending}
    >
      {renderContent()}
    </Modal>
  );
}
